/**
 * Tests if a string is likely a valid email address. Note the email format is
 * very lenient, but it also accepts higher-unicode emails where most regex
 * solutions return a false negative.
 * @param {string} value - the value to test
 * @return {boolean} if the value matches formatting for an email address
 */
export const isValidEmail = (value?: string) => {
  if (value) {
    return /.+@.+\..+/i.test(value);
  }
  return false;
};

export const getColorQueryOfEvent = (event: IEvent | Record<string, never>) =>
  event?.ColourSecondary && event?.ColourBackground
    ? `&background_color=${event.ColourSecondary.split('#')[1]}&font_color=${
        event.ColourBackground.split('#')[1]
      }`
    : '';

export const getRandomId = () => {
  return Math.random().toString(36).slice(2);
};

/**
 * Resolves the eventId from multiple sources with fallback priority.
 * This is critical for post-payment redirects where state may not be fully restored yet.
 *
 * Priority order:
 * 1. Extract from current pathname (most reliable during redirects)
 * 2. Fallback to router query params (may not be available on initial load)
 * 3. Fallback to event state (may be 0 during initial load)
 *
 * @param {Object} context - Resolution context
 * @param {any} [context.routerQuery] - Next.js router query object
 * @param {number} [context.eventIdFromState] - Event ID from application state
 * @returns {string | undefined} - Resolved eventId or undefined if unable to determine
 */
export const resolveEventIdFromContext = (context: {
  routerQuery?: any;
  eventIdFromState?: number;
}): string | undefined => {
  const { routerQuery, eventIdFromState } = context;
  const pathname = window?.location?.pathname;

  // 1: Extract from pathname (most reliable during redirects)
  if (pathname) {
    const pathMatch = pathname.match(/\/event\/(\d+)\//);
    if (pathMatch?.[1]) {
      return pathMatch[1];
    }
  }

  // 2: Fallback to router query (may not be available on initial load)
  if (routerQuery?.eventId) {
    return String(routerQuery.eventId);
  }

  // 3: Fallback to state (only if not 0 - initial state value)
  if (eventIdFromState && eventIdFromState !== 0) {
    return String(eventIdFromState);
  }

  return undefined;
};
